import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ACCESSORY_PRESETS,
  APERTURES,
  AUTO_APERTURE_ID,
  countFrames,
  estimatePoseRollMicros,
  EXPRESSIONS,
  PLACE_SCENE_PREFIX,
  type Expression,
  type Garment,
  type ModelWithStats,
  type Place,
  type PoseRef,
  type ShootSettings,
} from '@studio/shared';

import { api, assetUrl, type Presets } from '../lib/api';
import { useDateFormat } from '../lib/dates';
import { useErrorText } from '../lib/errors';
import { usePresetLabel, useT } from '../lib/i18n';
import { useMoney } from '../lib/money';
import { addPosePhoto } from '../lib/poses';
import { useSession } from '../lib/session';
import { DEFAULT_SETTINGS, defaultName } from '../lib/shootDefaults';
import { useExamples } from '../lib/useExamples';
import { ClothesGuide } from '../components/ClothesGuide';
import { ImageSource } from '../components/ImageSource';
import { LightPicker } from '../components/LookPicker';
import { OptionCard } from '../components/OptionCard';
import { Segmented } from '../components/Segmented';
import { ArrowRightIcon, PencilIcon, PlusIcon, SparkIcon, TrashIcon } from '../components/Icons';
import { useDialog } from '../components/Dialog';
import { useToast } from '../components/Toaster';
import { ModelEditor } from './Models';

type Step = 'model' | 'clothes' | 'poses' | 'places' | 'finish' | 'go';
const STEPS: Step[] = ['model', 'clothes', 'poses', 'places', 'finish', 'go'];
type Shelf = 'studio' | 'mine';
type LightFrom = NonNullable<ShootSettings['lightFrom']>;

/**
 * A shoot, set up one question at a time.
 *
 * The composer was one long page: everything on it at once, and the button at
 * the bottom. This asks in order — who, in what, in which poses, where, any
 * finishing — each question on one screen, with the count of what will be
 * shot always at the foot. Who and where come from the Studio's catalogue or
 * the client's own; a pose is a photograph, reproduced exactly with this
 * client's model, places and clothes.
 */
export function NewShootWizard() {
  const t = useT();
  const tp = usePresetLabel();
  const navigate = useNavigate();
  const toast = useToast();
  const money = useMoney();
  const when = useDateFormat();
  const describe = useErrorText();
  const examples = useExamples();
  const { user } = useSession();
  const staff = Boolean(user?.staff);

  const [step, setStep] = useState<Step>('model');
  const [presets, setPresets] = useState<Presets | undefined>();
  const [models, setModels] = useState<ModelWithStats[]>([]);
  const [places, setPlaces] = useState<Place[]>([]);
  const [poses, setPoses] = useState<PoseRef[]>([]);

  const [modelId, setModelId] = useState<string | undefined>();
  const [garments, setGarments] = useState<Garment[]>([]);
  const [scenes, setScenes] = useState<string[]>([]);
  const [poseIds, setPoseIds] = useState<string[]>([]);
  const [lightFrom, setLightFrom] = useState<LightFrom>('place');
  // Checked by default: a pose photo is shown only once its clothes and her face pass the judge.
  const [settings, setSettings] = useState<ShootSettings>({ ...DEFAULT_SETTINGS, qualityThreshold: 70 });
  const [expressions, setExpressions] = useState<Expression[]>(['auto']);
  const [name, setName] = useState('');

  const [creatingModel, setCreatingModel] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const loadModels = () =>
    api
      .listModels()
      .then((list) => {
        setModels(list);
        return list;
      })
      .catch(() => [] as ModelWithStats[]);

  useEffect(() => {
    void api
      .presets()
      .then(setPresets)
      .catch(() => undefined);
    void loadModels().then((list) => {
      if (list.length === 1 && list[0]) setModelId(list[0].id);
    });
    void api
      .listPlaces()
      .then(setPlaces)
      .catch(() => undefined);
    void api
      .listPoses()
      .then(setPoses)
      .catch(() => undefined);
  }, []);

  const set = <K extends keyof ShootSettings>(key: K, value: ShootSettings[K]) =>
    setSettings((prev) => ({ ...prev, [key]: value }));

  const chosenPoses = poseIds
    .map((id) => poses.find((p) => p.id === id))
    .filter((p): p is PoseRef => Boolean(p));
  const multiplied = expressions.filter((e) => e !== 'auto');
  const count = countFrames({
    modelIds: modelId ? [modelId] : [],
    povIds: [],
    variantsPerPov: 1,
    poseRefs: chosenPoses.map((p) => ({ id: p.id, povId: p.read.povId })),
    backdropIds: scenes,
    expressions: multiplied,
  });
  // One message to the image model a photograph: the reference of each place, then each other pose.
  const costAt = (fidelity: 'max' | 'standard') => estimatePoseRollMicros(count, { fidelity });
  const engine = settings.fidelity === 'standard' ? 'standard' : 'max';
  const cost = costAt(engine);
  /** What a photo costs on average with each renderer, said beside its name. */
  const perPhoto = (fidelity: 'max' | 'standard') =>
    count > 0 ? costAt(fidelity) / count : estimatePoseRollMicros(1, { fidelity });
  const breakdown = [
    t(chosenPoses.length === 1 ? 'wiz.n.pose' : 'wiz.n.poses', { n: chosenPoses.length }),
    t(scenes.length === 1 ? 'wiz.n.place' : 'wiz.n.places', { n: scenes.length }),
    multiplied.length > 1 ? t('plan.n.expressions', { n: multiplied.length }) : '',
  ]
    .filter(Boolean)
    .join(' × ');

  /** What each step still needs before the next, if anything. */
  const missing: Partial<Record<Step, string>> = {
    model: modelId ? undefined : t('wiz.need.model'),
    clothes: garments.length ? undefined : t('wiz.need.clothes'),
    places: scenes.length ? undefined : t('wiz.need.places'),
    poses: chosenPoses.length ? undefined : t('wiz.need.poses'),
  };
  const index = STEPS.indexOf(step);
  const blocker = missing[step];
  /** A step can be visited when every step before it is answered. */
  const reachable = (target: Step) => {
    const until = STEPS.indexOf(target);
    return STEPS.slice(0, until).every((s) => !missing[s]);
  };

  const submit = async () => {
    if (!modelId) return;
    setSubmitting(true);
    setError('');
    try {
      const shoot = await api.createShoot({
        name: name.trim() || defaultName(garments, t, when.short),
        modelIds: [modelId],
        garmentIds: garments.map((g) => g.id),
        povIds: [...new Set(chosenPoses.map((p) => p.read.povId))],
        settings: {
          ...settings,
          mode: 'poses',
          variantsPerPov: 1,
          poseIds: [],
          // The server replaces these ids with its own copy of each pose.
          poseRefs: chosenPoses.map((p) => ({ id: p.id })) as unknown as ShootSettings['poseRefs'],
          backdropId: scenes[0] ?? settings.backdropId,
          backdropIds: scenes.length > 1 ? scenes : undefined,
          lightFrom,
          expression: multiplied[0] ?? 'auto',
          expressions: multiplied.length > 1 ? multiplied : undefined,
        },
        start: true,
      });
      toast(t(count === 1 ? 'shoot.rollingOne' : 'shoot.rolling', { n: count, name: shoot.name }), 'ok');
      navigate(`/shoots/${shoot.id}`);
    } catch (err) {
      setError(describe(err, 'shoot.failed'));
      setSubmitting(false);
    }
  };

  const next = () => {
    if (blocker) return;
    if (step === 'go') void submit();
    else setStep(STEPS[index + 1]!);
  };

  const model = models.find((m) => m.id === modelId);

  return (
    <div className="wiz">
      <nav className="wiz__steps" aria-label={t('wiz.title')}>
        {STEPS.map((s, i) => (
          <button
            key={s}
            type="button"
            className={`wiz__step${s === step ? ' is-now' : ''}${i < index && !missing[s] ? ' is-done' : ''}`}
            aria-current={s === step ? 'step' : undefined}
            disabled={!reachable(s)}
            onClick={() => setStep(s)}
          >
            <b>{i + 1}</b>
            <span>{t(`wiz.step.${s}`)}</span>
          </button>
        ))}
      </nav>

      <section className="wiz__body" aria-live="polite">
        <header className="wiz__head">
          <h1 className="wiz__title">{t(`wiz.title.${step}`)}</h1>
          <p className="wiz__lede">{t(`wiz.lede.${step}`)}</p>
        </header>

        {error && <div className="banner banner--error">{error}</div>}

        {step === 'model' && (
          <>
            <ModelStep
              models={models}
              selected={modelId}
              staff={staff}
              onSelect={setModelId}
              onCreate={() => setCreatingModel(true)}
            />
            <p className="wiz__aside">
              <Link to="/new/advanced">{t('wiz.advanced')}</Link>
            </p>
          </>
        )}

        {step === 'clothes' && <ClothesGuide garments={garments} onChange={setGarments} />}

        {step === 'places' && (
          <PlacesStep
            scenes={presets?.backdrops ?? []}
            places={places}
            selected={scenes}
            examples={examples}
            onToggle={(id) =>
              setScenes((list) => (list.includes(id) ? list.filter((s) => s !== id) : [...list, id]))
            }
            onAdded={(place) => {
              setPlaces((list) => [place, ...list]);
              setScenes((list) => [...list, `${PLACE_SCENE_PREFIX}${place.id}`]);
            }}
            onRenamed={(place) => setPlaces((list) => list.map((p) => (p.id === place.id ? place : p)))}
            onDeleted={(id) => {
              setPlaces((list) => list.filter((p) => p.id !== id));
              setScenes((list) => list.filter((s) => s !== `${PLACE_SCENE_PREFIX}${id}`));
            }}
          />
        )}

        {step === 'poses' && (
          <PosesStep
            poses={poses}
            selected={poseIds}
            staff={staff}
            onToggle={(id) =>
              setPoseIds((list) => (list.includes(id) ? list.filter((p) => p !== id) : [...list, id]))
            }
            onAdded={(pose) => {
              setPoses((list) => [pose, ...list]);
              setPoseIds((list) => [...list, pose.id]);
            }}
            onRenamed={(pose) => setPoses((list) => list.map((p) => (p.id === pose.id ? pose : p)))}
            onDeleted={(id) => {
              setPoses((list) => list.filter((p) => p.id !== id));
              setPoseIds((list) => list.filter((p) => p !== id));
            }}
          />
        )}

        {step === 'finish' && (
          <div className="wiz__pane wiz__finish">
            <Field label={t('wiz.light')}>
              <div className="chips">
                {(['place', 'pose', 'chosen'] as const).map((id) => (
                  <button
                    key={id}
                    type="button"
                    className={`chip ${lightFrom === id ? 'is-on' : ''}`}
                    aria-pressed={lightFrom === id}
                    onClick={() => setLightFrom(id)}
                  >
                    {t(`wiz.lightFrom.${id}`)}
                  </button>
                ))}
              </div>
              {lightFrom === 'chosen' && presets && (
                <LightPicker
                  lighting={presets.lighting}
                  selected={settings.lightingId}
                  onChange={(id) => set('lightingId', id)}
                />
              )}
            </Field>

            {/*
             * What a photograph costs, said where it is chosen: the stronger
             * renderer, or the newer Flash one at about half its price. Every
             * pose photo is checked either way — none is offered until its
             * clothes and her face pass the judge.
             */}
            <Field label={t('wiz.quality')}>
              <Segmented
                options={['max', 'standard'] as const}
                value={engine}
                onChange={(fidelity) => set('fidelity', fidelity)}
                label={t('wiz.quality')}
                format={(fidelity) => `${t(`wiz.engine.${fidelity}`)} · ${money.cost(perPhoto(fidelity))}`}
              />
              <p className="faint">
                {t(`wiz.engineHint.${engine}`)}{' '}
                {t('wiz.qualityHint.checked', { cost: money.cost(perPhoto(engine)) })}
              </p>
            </Field>

            <Field label={t('shoot.styling')}>
              <Segmented
                options={['as-uploaded', 'complete'] as const}
                value={settings.stylingMode ?? 'as-uploaded'}
                onChange={(mode) => set('stylingMode', mode)}
                label={t('shoot.styling')}
                format={(mode) => t(`enum.styling.${mode}`)}
              />
            </Field>

            <Field
              label={t('shoot.expression')}
              hint={multiplied.length > 1 ? t('wiz.severalExpr') : undefined}
            >
              <div className="chips">
                {EXPRESSIONS.map(({ id }) => {
                  const on = expressions.includes(id);
                  return (
                    <button
                      key={id}
                      type="button"
                      className={`chip ${on ? 'is-on' : ''}`}
                      aria-pressed={on}
                      onClick={() =>
                        setExpressions((list) => {
                          if (id === 'auto') return ['auto'];
                          const rest = list.filter((e) => e !== 'auto');
                          const nextList = rest.includes(id) ? rest.filter((e) => e !== id) : [...rest, id];
                          return nextList.length ? nextList : ['auto'];
                        })
                      }
                    >
                      {id === 'auto' ? t('wiz.asInPose') : t(`enum.expression.${id}`)}
                    </button>
                  );
                })}
              </div>
            </Field>

            <Field label={t('shoot.accessories')}>
              <div className="chips">
                {ACCESSORY_PRESETS.map((accessory) => {
                  const chosen = settings.accessories ?? [];
                  const on = chosen.includes(accessory.id);
                  return (
                    <button
                      key={accessory.id}
                      type="button"
                      className={`chip ${on ? 'is-on' : ''}`}
                      aria-pressed={on}
                      onClick={() =>
                        set(
                          'accessories',
                          on ? chosen.filter((x) => x !== accessory.id) : [...chosen, accessory.id],
                        )
                      }
                    >
                      {tp(`accessory.${accessory.id}`, accessory.name)}
                    </button>
                  );
                })}
              </div>
            </Field>

            <Field label={t('shoot.colour')}>
              <div className="chips">
                {(presets?.grades ?? []).map((grade) => (
                  <button
                    key={grade.id}
                    type="button"
                    className={`chip ${settings.gradeId === grade.id ? 'is-on' : ''}`}
                    aria-pressed={settings.gradeId === grade.id}
                    onClick={() => set('gradeId', grade.id)}
                  >
                    {tp(`grade.${grade.id}.name`, grade.name)}
                  </button>
                ))}
              </div>
            </Field>

            <ApertureField settings={settings} onChange={setSettings} />

            <Field label={t('shoot.notes')}>
              <textarea
                className="textarea"
                rows={2}
                value={settings.details}
                placeholder={t('shoot.notesPlaceholder')}
                onChange={(event) => set('details', event.target.value)}
              />
            </Field>
          </div>
        )}

        {step === 'go' && (
          <div className="wiz__pane wiz__recap">
            <div className="recap">
              <RecapRow label={t('wiz.step.model')} onEdit={() => setStep('model')} t={t}>
                {model && (
                  <span className="recap__item">
                    <Thumb assetId={portraitOf(model)} />
                    {model.name}
                  </span>
                )}
              </RecapRow>
              <RecapRow label={t('wiz.step.clothes')} onEdit={() => setStep('clothes')} t={t}>
                {garments.map((g) => (
                  <span key={g.id} className="recap__item">
                    <Thumb assetId={g.assetId} />
                    {g.label}
                  </span>
                ))}
              </RecapRow>
              <RecapRow label={t('wiz.step.places')} onEdit={() => setStep('places')} t={t}>
                {scenes.map((id) => (
                  <span key={id} className="recap__item">
                    {sceneLabel(id, presets, places, tp)}
                  </span>
                ))}
              </RecapRow>
              <RecapRow label={t('wiz.step.poses')} onEdit={() => setStep('poses')} t={t}>
                {chosenPoses.map((p) => (
                  <span key={p.id} className="recap__item">
                    <Thumb assetId={p.assetId} />
                    {p.name}
                  </span>
                ))}
              </RecapRow>
            </div>
            <div className="field">
              <label htmlFor="wiz-name">{t('wiz.name')}</label>
              <input
                id="wiz-name"
                className="input"
                value={name}
                placeholder={defaultName(garments, t, when.short)}
                onChange={(event) => setName(event.target.value)}
              />
            </div>
          </div>
        )}
      </section>

      <footer className="wiz__foot">
        <button
          type="button"
          className="btn btn--ghost"
          disabled={index === 0}
          onClick={() => setStep(STEPS[Math.max(0, index - 1)]!)}
        >
          {t('wiz.back')}
        </button>
        <div className="wiz__count">
          <b>{count}</b>
          <span>
            {t(count === 1 ? 'common.frame' : 'common.frames')}
            {count > 0 && <> · {money.cost(cost)}</>}
          </span>
          <small>{blocker ?? (count > 0 ? breakdown : '')}</small>
        </div>
        <button
          type="button"
          className="btn btn--primary wiz__next"
          disabled={Boolean(blocker) || submitting}
          onClick={next}
        >
          {submitting ? <span className="spinner" /> : step === 'go' ? <SparkIcon /> : null}
          {step === 'go' ? t(submitting ? 'shoot.going' : 'shoot.go') : t('wiz.next')}
          {step !== 'go' && <ArrowRightIcon />}
        </button>
      </footer>

      {creatingModel && (
        <ModelEditor
          onClose={() => setCreatingModel(false)}
          onSaved={(message) => {
            setCreatingModel(false);
            toast(message, 'ok');
            void loadModels().then((list) => {
              // The newest of the account's own is the one just made.
              const made = list.find((m) => !m.house);
              if (made) setModelId(made.id);
            });
          }}
          onChanged={() => void loadModels()}
          toast={toast}
          t={t}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */

export function portraitOf(model: ModelWithStats): string | undefined {
  return model.referenceAssetIds[0] ?? model.views?.find((v) => v.kind === 'front')?.assetId;
}

export function Thumb({ assetId }: { assetId?: string }) {
  return <span className="recap__thumb">{assetId && <img src={assetUrl(assetId)} alt="" />}</span>;
}

/**
 * The one finish chosen before a roll is shot: the lens, said to the renderer
 * because only it knows what is near her and what is far. Film, grain and
 * vignette are not asked here: they are developed after the render, on each
 * photograph, where they can be judged by eye and changed for nothing.
 */
export function ApertureField({
  settings,
  onChange,
}: {
  settings: ShootSettings;
  onChange: (update: (prev: ShootSettings) => ShootSettings) => void;
}) {
  const t = useT();
  const tp = usePresetLabel();
  const aperture = APERTURES.find((a) => a.id === (settings.aperture ?? AUTO_APERTURE_ID)) ?? APERTURES[0]!;
  return (
    <Field label={t('shoot.aperture')} hint={t('shoot.apertureHint')}>
      <div className="chips">
        {APERTURES.map((option) => (
          <button
            key={option.id}
            type="button"
            className={`chip ${aperture.id === option.id ? 'is-on' : ''}`}
            aria-pressed={aperture.id === option.id}
            onClick={() => onChange((prev) => ({ ...prev, aperture: option.id }))}
          >
            {option.id === AUTO_APERTURE_ID ? t('common.auto') : option.name}
          </button>
        ))}
      </div>
      <p className="faint">{tp(`aperture.${aperture.id}`, aperture.description)}</p>
    </Field>
  );
}

export function Dial({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: number;
  onChange: (value: number) => void;
}) {
  return (
    <div className="dial">
      <label className="dial__label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className="dial__range"
        type="range"
        min={0}
        max={100}
        step={1}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
      />
      <span className="dial__value">{Math.round(value)}</span>
    </div>
  );
}

export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="wiz__field">
      <span className="label">{label}</span>
      {children}
      {hint && <p className="faint">{hint}</p>}
    </div>
  );
}

export function RecapRow({
  label,
  onEdit,
  t,
  children,
}: {
  label: string;
  onEdit: () => void;
  t: ReturnType<typeof useT>;
  children: ReactNode;
}) {
  return (
    <div className="recap__row">
      <span className="recap__label">{label}</span>
      <div className="recap__items">{children}</div>
      <button type="button" className="btn btn--link btn--sm" onClick={onEdit}>
        {t('wiz.edit')}
      </button>
    </div>
  );
}

function sceneLabel(
  id: string,
  presets: Presets | undefined,
  places: Place[],
  tp: (key: string, fallback: string) => string,
): string {
  if (id.startsWith(PLACE_SCENE_PREFIX)) {
    return places.find((p) => `${PLACE_SCENE_PREFIX}${p.id}` === id)?.name ?? id;
  }
  const scene = presets?.backdrops.find((b) => b.id === id);
  return tp(`scene.${id}.name`, scene?.name ?? id);
}

/** The Studio's or the client's own: two shelves of one library. */
function ShelfChips({
  shelf,
  onChange,
  counts,
  label,
}: {
  shelf: Shelf;
  onChange: (shelf: Shelf) => void;
  counts: Record<Shelf, number>;
  /** What the offered shelf holds — "Models", "Places", "Poses" — rather than whose it is. */
  label: string;
}) {
  const t = useT();
  return (
    <div className="chips" role="tablist">
      {(['studio', 'mine'] as const).map((id) => (
        <button
          key={id}
          type="button"
          role="tab"
          aria-selected={shelf === id}
          className={`chip ${shelf === id ? 'is-on' : ''}`}
          onClick={() => onChange(id)}
        >
          {id === 'studio' ? label : t('wiz.shelf.mine')} <b>{counts[id]}</b>
        </button>
      ))}
    </div>
  );
}

/* ------------------------------ model ------------------------------ */

export function ModelStep({
  models,
  selected,
  staff,
  onSelect,
  onCreate,
}: {
  models: ModelWithStats[];
  staff: boolean;
  selected?: string;
  onSelect: (id: string) => void;
  onCreate: () => void;
}) {
  const t = useT();
  const studio = models.filter((m) => m.house);
  const mine = models.filter((m) => !m.house);
  const [shelf, setShelf] = useState<Shelf>(mine.length && !studio.length ? 'mine' : 'studio');
  useEffect(() => {
    if (!studio.length && mine.length) setShelf('mine');
  }, [studio.length, mine.length]);
  const shown = shelf === 'studio' ? studio : mine;

  return (
    <div className="wiz__pane">
      <ShelfChips
        shelf={shelf}
        onChange={setShelf}
        counts={{ studio: studio.length, mine: mine.length }}
        label={t('wiz.shelf.models')}
      />
      <div className="wiz__grid">
        {shelf === 'mine' && (
          <button type="button" className="wiz__add" onClick={onCreate}>
            <PlusIcon />
            <span>{t('wiz.addModel')}</span>
          </button>
        )}
        {shown.map((model) => {
          const portrait = portraitOf(model);
          return (
            <OptionCard
              key={model.id}
              title={model.name}
              spec={model.house ? t('wiz.studio') : undefined}
              selected={selected === model.id}
              image={portrait ? assetUrl(portrait) : undefined}
              fallback={<span className="wiz__initial">{model.name.charAt(0)}</span>}
              onToggle={() => onSelect(model.id)}
            />
          );
        })}
        {shown.length === 0 && shelf === 'studio' && (
          <p className="faint wiz__empty">
            {t(staff ? 'wiz.emptyStudioModelsStaff' : 'wiz.emptyStudioModels')}
          </p>
        )}
      </div>
    </div>
  );
}

/* ------------------------------ places ----------------------------- */

function PlacesStep({
  scenes,
  places,
  selected,
  examples,
  onToggle,
  onAdded,
  onRenamed,
  onDeleted,
}: {
  scenes: Presets['backdrops'];
  places: Place[];
  selected: string[];
  examples: Record<string, string>;
  onToggle: (id: string) => void;
  onAdded: (place: Place) => void;
  onRenamed: (place: Place) => void;
  onDeleted: (id: string) => void;
}) {
  const t = useT();
  const tp = usePresetLabel();
  const describe = useErrorText();
  const [shelf, setShelf] = useState<Shelf>('studio');
  /** The Studio's shelf is long; narrowed by kind of place. */
  const [kind, setKind] = useState<'all' | 'street' | 'studio'>('all');
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [assetId, setAssetId] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const studioPlaces = places.filter((p) => p.house);
  const mine = places.filter((p) => !p.house);
  const order = (id: string) => (selected.includes(id) ? selected.indexOf(id) + 1 : undefined);

  const save = async () => {
    setBusy(true);
    setError('');
    try {
      const place = await api.createPlace({
        name: name.trim(),
        description: description.trim(),
        assetId,
      });
      onAdded(place);
      setAdding(false);
      setName('');
      setDescription('');
      setAssetId(undefined);
      setShelf('mine');
    } catch (err) {
      setError(describe(err, 'wiz.placeFailed'));
    } finally {
      setBusy(false);
    }
  };

  if (adding) {
    return (
      <div className="wiz__pane wiz__form">
        <div className="wiz__formPhoto">
          <ImageSource
            kind="backdrop"
            assetId={assetId}
            onChange={(asset) => setAssetId(asset?.id)}
            prompt={t('wiz.placePhoto')}
            ratio="4 / 3"
          />
        </div>
        <div className="wiz__formFields">
          <div className="field">
            <label htmlFor="place-name">{t('wiz.placeName')}</label>
            <input
              id="place-name"
              className="input"
              value={name}
              placeholder={t('wiz.placeNamePlaceholder')}
              onChange={(event) => setName(event.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="place-desc">{t('wiz.placeDesc')}</label>
            <textarea
              id="place-desc"
              className="textarea"
              rows={3}
              value={description}
              placeholder={t('wiz.placeDescPlaceholder')}
              onChange={(event) => setDescription(event.target.value)}
            />
          </div>
          {error && <div className="banner banner--error">{error}</div>}
          <div className="row">
            <button type="button" className="btn btn--ghost" onClick={() => setAdding(false)}>
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="btn btn--primary"
              disabled={busy || !name.trim() || (!description.trim() && !assetId)}
              onClick={() => void save()}
            >
              {busy ? <span className="spinner" /> : null}
              {t('wiz.placeSave')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const shownScenes = scenes.filter((scene) =>
    kind === 'all' ? true : kind === 'street' ? scene.kind === 'location' : scene.kind === 'studio',
  );

  return (
    <div className="wiz__pane">
      <div className="row row--wrap" style={{ gap: 12 }}>
        <ShelfChips
          shelf={shelf}
          onChange={setShelf}
          counts={{ studio: scenes.length + studioPlaces.length, mine: mine.length }}
          label={t('wiz.shelf.places')}
        />
        {shelf === 'studio' && (
          <div className="chips wiz__kinds">
            {(['all', 'street', 'studio'] as const).map((id) => (
              <button
                key={id}
                type="button"
                className={`chip chip--quiet ${kind === id ? 'is-on' : ''}`}
                aria-pressed={kind === id}
                onClick={() => setKind(id)}
              >
                {t(`wiz.kind.${id}`)}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="wiz__grid">
        {shelf === 'mine' && (
          <button type="button" className="wiz__add" onClick={() => setAdding(true)}>
            <PlusIcon />
            <span>{t('wiz.addPlace')}</span>
          </button>
        )}
        {(shelf === 'studio' ? (kind === 'all' ? studioPlaces : []) : mine).map((place) => {
          const id = `${PLACE_SCENE_PREFIX}${place.id}`;
          return (
            <LibraryCard
              key={place.id}
              removable={!place.house}
              onRemove={() =>
                void api
                  .deletePlace(place.id)
                  .then(() => onDeleted(place.id))
                  .catch(() => undefined)
              }
              removeLabel={t('wiz.removePlace', { name: place.name })}
              name={place.name}
              onRename={(next) => api.updatePlace(place.id, { name: next }).then(onRenamed)}
            >
              <OptionCard
                caption={false}
                title={place.name}
                description={place.description}
                selected={selected.includes(id)}
                order={order(id)}
                image={place.assetId ? assetUrl(place.assetId) : undefined}
                fallback={<span className="wiz__initial">{place.name.charAt(0)}</span>}
                onToggle={() => onToggle(id)}
              />
            </LibraryCard>
          );
        })}
        {shelf === 'studio' &&
          shownScenes.map((scene) => (
            <OptionCard
              key={scene.id}
              caption={false}
              title={tp(`scene.${scene.id}.name`, scene.name)}
              selected={selected.includes(scene.id)}
              order={order(scene.id)}
              example={examples[`scene-${scene.id}`]}
              swatch={scene.swatch}
              onToggle={() => onToggle(scene.id)}
            />
          ))}
        {shelf === 'mine' && mine.length === 0 && (
          <p className="faint wiz__empty">{t('wiz.emptyMyPlaces')}</p>
        )}
      </div>
    </div>
  );
}

/* ------------------------------- poses ----------------------------- */

function PosesStep({
  poses,
  selected,
  staff,
  onToggle,
  onAdded,
  onRenamed,
  onDeleted,
}: {
  poses: PoseRef[];
  selected: string[];
  staff: boolean;
  onToggle: (id: string) => void;
  onAdded: (pose: PoseRef) => void;
  onRenamed: (pose: PoseRef) => void;
  onDeleted: (id: string) => void;
}) {
  const t = useT();
  const describe = useErrorText();
  const studio = poses.filter((p) => p.house);
  const mine = poses.filter((p) => !p.house);
  const [shelf, setShelf] = useState<Shelf>(studio.length || !mine.length ? 'studio' : 'mine');
  const [adding, setAdding] = useState(0);
  const [error, setError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const shown = useMemo(() => (shelf === 'studio' ? studio : mine), [shelf, studio, mine]);

  /* Each photograph is a pose as soon as it is in: nothing to fill in, nothing read. */
  const add = async (files: FileList | null) => {
    const list = Array.from(files ?? []).slice(0, 12);
    if (fileRef.current) fileRef.current.value = '';
    if (!list.length) return;
    setError('');
    setAdding(list.length);
    for (const file of list) {
      try {
        onAdded(await addPosePhoto(file));
        setShelf('mine');
      } catch (err) {
        setError(describe(err, 'wiz.poseFailed'));
      }
      setAdding((n) => n - 1);
    }
  };

  return (
    <div className="wiz__pane">
      <div className="row row--wrap row--between">
        <ShelfChips
          shelf={shelf}
          onChange={setShelf}
          counts={{ studio: studio.length, mine: mine.length }}
          label={t('wiz.shelf.poses')}
        />
      </div>
      {error && <div className="banner banner--error">{error}</div>}
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        multiple
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(event) => void add(event.target.files)}
      />
      <div className="wiz__grid">
        {shelf === 'mine' && (
          <button
            type="button"
            className="wiz__add"
            disabled={adding > 0}
            onClick={() => fileRef.current?.click()}
          >
            {adding > 0 ? <span className="spinner" /> : <PlusIcon />}
            <span>{adding > 0 ? t('wiz.poseAdding', { n: adding }) : t('wiz.addPose')}</span>
            {adding === 0 && <small>{t('wiz.addPoseHint')}</small>}
          </button>
        )}
        {shown.map((pose) => (
          <LibraryCard
            key={pose.id}
            removable={!pose.house}
            onRemove={() =>
              void api
                .deletePose(pose.id)
                .then(() => onDeleted(pose.id))
                .catch(() => undefined)
            }
            removeLabel={t('wiz.removePose', { name: pose.name })}
            name={pose.name}
            onRename={(next) => api.renamePose(pose.id, next).then(onRenamed)}
          >
            <OptionCard
              caption={false}
              title={pose.name}
              selected={selected.includes(pose.id)}
              order={selected.includes(pose.id) ? selected.indexOf(pose.id) + 1 : undefined}
              image={assetUrl(pose.assetId)}
              onToggle={() => onToggle(pose.id)}
            />
          </LibraryCard>
        ))}
        {shown.length === 0 && (
          <p className="faint wiz__empty">
            {t(
              shelf === 'studio'
                ? staff
                  ? 'wiz.emptyStudioPosesStaff'
                  : 'wiz.emptyStudioPoses'
                : 'wiz.emptyMyPoses',
            )}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * A library card with a pencil and a small bin, for what this account may
 * change: renaming asks for the new name; deleting asks first, it is for good.
 */
function LibraryCard({
  removable,
  onRemove,
  removeLabel,
  name,
  onRename,
  children,
}: {
  removable: boolean;
  onRemove: () => void;
  removeLabel: string;
  name: string;
  onRename: (name: string) => Promise<unknown>;
  children: ReactNode;
}) {
  const t = useT();
  const toast = useToast();
  const describe = useErrorText();
  const { ask, prompt } = useDialog();
  const rename = async () => {
    const next = await prompt({
      title: t('wiz.rename'),
      label: t('wiz.placeName'),
      value: name,
      confirmLabel: t('common.save'),
    });
    if (!next?.trim() || next.trim() === name) return;
    try {
      await onRename(next.trim());
    } catch (err) {
      toast(describe(err, 'wiz.renameFailed'), 'error');
    }
  };
  const remove = async () => {
    if (
      await ask({
        title: `${removeLabel} ?`,
        body: t('wiz.removeBody'),
        confirmLabel: t('wiz.removeGo'),
        danger: true,
      })
    )
      onRemove();
  };
  return (
    <div className="wiz__card">
      {children}
      {removable && (
        <button
          type="button"
          className="wiz__rename"
          aria-label={t('wiz.renameNamed', { name })}
          title={t('wiz.renameNamed', { name })}
          onClick={() => void rename()}
        >
          <PencilIcon />
        </button>
      )}
      {removable && (
        <button
          type="button"
          className="wiz__remove"
          aria-label={removeLabel}
          title={removeLabel}
          onClick={() => void remove()}
        >
          <TrashIcon />
        </button>
      )}
    </div>
  );
}
